/**
 * Play view (task 20.3) — the in-session screen.
 *
 * Composes the four Phase-1 play pieces the requirements call for:
 *   (a) the JOURNAL — the full, unabridged chat log (R7.1), fed by every push,
 *   (b) the FREE-FLOW SHARED CHAT input (R31.2),
 *   (c) the IN-APP DICE ROLLER — a per-roll choice, tap or report (R2.1/R2.2),
 *       whose result is recorded in the audit trail (R2.3),
 *   (d) the SESSION STATUS — the "Rufus is writing…" indicator (task 14.5) and
 *       the clean retry state (R13.5; task 20.4 enriches these).
 *
 * Results (DM narration, turn results, retries) arrive over the WebSocket push
 * channel via {@link useCampaignSession}, never a blocking HTTP response
 * (Design: results delivered over WS).
 */

import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useServices } from "../AppContext.js";
import { useCampaignSession } from "../useCampaignSession.js";
import { Journal } from "../components/Journal.js";
import { FreeFlowChat } from "../components/FreeFlowChat.js";
import { DiceRoller } from "../components/DiceRoller.js";
import { SessionStatus } from "../components/SessionStatus.js";
import { IssueReporter } from "../components/IssueReporter.js";
import { MapPanel } from "../map/index.js";
import { TimeWeatherBadge } from "../components/TimeWeatherBadge.js";
import { rollChatTag, type RollContext } from "../dice.js";
import type { CampaignState, MessageMode } from "@rufus/shared-types";
import { friendlyErrorMessage, type DiceRollRequest, type DiceRollResponse } from "../api-client.js";
import {
  setTelemetryAnchor,
  clearTelemetryAnchor,
  recordNavigationBreadcrumb,
} from "../telemetry.js";

/**
 * Refresh the id token before a WS reconnect if it is within this window of
 * expiry (mirrors the REST client's proactive window in main.tsx). A
 * mid-session drop is often an expired token; refreshing here means the
 * reconnect carries a fresh bearer on the first try instead of eating a
 * rejected handshake.
 */
const WS_PROACTIVE_REFRESH_WINDOW_MS = 60_000;

export function PlayScreen() {
  const { config, api, auth } = useServices();
  const { campaignId = "" } = useParams();

  // Tracks whether this view is still mounted, so the async `send` `finally`
  // (which retires the turn anchor to the campaign) does NOT re-pin a stale
  // anchor after the player has navigated away mid-turn and the unmount cleanup
  // already cleared it (round-4 Low #9). Without the guard, the ordering is:
  // unmount cleanup clears the anchor → the in-flight submit settles → the
  // `finally` re-sets `{campaignId}` → an unrelated later crash files against a
  // campaign the player has left.
  const mountedRef = useRef(true);

  // Publish the current campaign as the telemetry crash anchor while this view
  // is mounted, so a crash during play auto-files an issue pointed at this
  // campaign (the triager can pull the surrounding transcript). Cleared on
  // unmount so a crash on a non-campaign screen carries no stale anchor.
  useEffect(() => {
    mountedRef.current = true;
    if (campaignId) {
      setTelemetryAnchor({ campaignId });
      // Record the route change in the crash trail (path-only, secret-free).
      recordNavigationBreadcrumb(`/campaigns/${campaignId}/play`);
    }
    return () => {
      mountedRef.current = false;
      clearTelemetryAnchor();
    };
  }, [campaignId]);

  // Re-read the token whenever auth changes (a refresh, e.g. triggered by a
  // rejected WS handshake, mints a fresh id token). This state update re-renders
  // with the new `auth.idToken`, which re-fires the session effect (keyed on the
  // token) so the socket reconnects with the fresh token instead of looping on
  // the expired one.
  const [idToken, setIdToken] = useState<string | undefined>(auth.idToken ?? undefined);
  useEffect(() => {
    const off = auth.onChange(() => setIdToken(auth.idToken ?? undefined));
    // Reconcile in case the token changed between render and effect attach.
    setIdToken(auth.idToken ?? undefined);
    return off;
  }, [auth]);

  const session = useCampaignSession(campaignId, {
    url: config.wsUrl,
    // LIVE token (read fresh on every reconnect) so a refresh is picked up
    // without a client rebuild. `idToken` state still drives the effect's
    // auth-PRESENCE dep (tear down the socket on logout) + other UI.
    token: idToken,
    getToken: () => auth.idToken,
    // On a rejected handshake (expired/invalid token), refresh once; on success
    // the WsClient reconnects itself with the fresh live token.
    onUnauthorized: () => auth.refresh(),
    // Mid-session drop: if the id token is near/at expiry, refresh BEFORE the
    // backoff reconnect so it carries a fresh token on the first try (no wasted
    // rejected handshake). Best-effort — the client swallows any error.
    onBeforeReconnect: async () => {
      if (auth.isExpiringWithin(WS_PROACTIVE_REFRESH_WINDOW_MS)) {
        await auth.refresh();
      }
    },
  });

  // HYDRATE the journal on load so a returning player sees where they left off
  // instead of a blank log (the live journal is WebSocket-push-only, so a page
  // refresh starts it empty). Fetch the rolling summary + the lines since the
  // summary watermark and seed them; each entry is keyed by conversation seq, so
  // a later live push for the same seq merges in place rather than duplicating.
  // BEST-EFFORT: a failed read just leaves the push-only behavior — it must never
  // break the play view. Guarded against a resolve after unmount / campaign swap.
  // Also re-run on RECONNECT (`session.reconnectNonce` bumps each time the socket
  // drops and comes back): a result pushed while the socket was down was never
  // delivered, so catching the log back up here is what recovers the missed
  // narration AND clears a stale retry banner — without the player refreshing.
  const { hydrate, reconnectNonce } = session;
  useEffect(() => {
    if (!campaignId) return;
    let cancelled = false;
    api
      .getJournal(campaignId)
      .then((history) => {
        if (!cancelled) hydrate(campaignId, history);
      })
      .catch(() => {
        // No history (new campaign), a 404 (non-member), or a transient read
        // failure — the journal simply fills from live pushes going forward.
      });
    return () => {
      cancelled = true;
    };
  }, [api, campaignId, hydrate, reconnectNonce]);

  // A player-safe message for a failed action (submit turn / roll). The turn
  // ingress can refuse with a 402 (insufficient credits), 409 (campaign paused),
  // 403 (moderation) etc.; those must surface as readable text, not an uncaught
  // promise rejection in the console.
  const [actionError, setActionError] = useState<string | null>(null);

  // The composer draft, lifted here so a resolved dice roll can APPEND its tag
  // (e.g. `[d20 roll of 5]`) into whatever the player is already typing — the
  // player then adds their words and sends on their own terms (R2.1/R2.3). The
  // composer clears this back to "" on send.
  const [draft, setDraft] = useState("");

  // The player's acting character in THIS campaign. Resolved from the caller's
  // roster: the owned character whose `currentCampaignId` points at this campaign
  // is the one they brought in (the same membership signal the reads layer uses).
  // The dice endpoint needs this real id — a placeholder like "active" 404s.
  const [actingCharacterId, setActingCharacterId] = useState<string | null>(null);
  // authorId (a user id, as it arrives on chat pushes) -> display name, so the
  // journal shows a readable name instead of a raw id. Built from the caller's
  // roster: a character the caller controls maps its controllerUserId -> name.
  // Phase-1 limit: this resolves the CALLER's own authored lines (and any owned
  // characters); other players' ids fall back to the raw id until a
  // members-with-names read exists. Graceful either way.
  const [authorNames, setAuthorNames] = useState<Record<string, string>>({});
  // The campaign's display name, when the stored state carries one (older
  // campaigns created before the name was persisted have none → undefined, and
  // the heading falls back to the id).
  const [campaignName, setCampaignName] = useState<string | undefined>(undefined);
  // The campaign's current state (for the in-world clock + weather badge). The
  // play view is driven by the WS push channel; this is a one-shot read for the
  // read-only time/weather display and is refreshed when the seqToken advances.
  const [campaign, setCampaign] = useState<CampaignState | undefined>(undefined);
  // ROSTER read — ON MOUNT ONLY (two-model gate Medium #8). Resolves the acting
  // character + the authorId→name map, which are stable membership facts, not
  // per-turn state. Keeping this off the seqToken dep means a single transient
  // read failure mid-session cannot strip `actingCharacterId` (which would 404
  // the dice roller) or regress journal bylines to raw ids every turn — the
  // once-per-mount failure profile the earlier combined effect accidentally made
  // once-per-turn. A failure here leaves the roller disabled + id-fallbacks in
  // place; the WS push channel still drives play.
  useEffect(() => {
    let cancelled = false;
    api
      .listCharacters()
      .then((roster) => {
        if (cancelled) return;
        const acting = roster.find((c) => c.currentCampaignId === campaignId);
        setActingCharacterId(acting?.characterId ?? null);
        // authorId→name map for journal bylines. SCOPE TO THIS CAMPAIGN: the
        // roster is the WHOLE account (every character the user owns across all
        // their campaigns), and every one shares the same controllerUserId, so a
        // naive `names[controllerUserId] = c.name` over the full roster is
        // last-write-wins and can label the player's own messages with a
        // DIFFERENT campaign's character name (the cross-campaign byline bug —
        // a user with characters "Newt" in one game and "Tom2" in this one saw
        // their line bylined "Newt"). Only map characters that belong to THIS
        // campaign so a user's byline is always the character they play HERE.
        const names: Record<string, string> = {};
        for (const c of roster) {
          if (c.controllerUserId && c.currentCampaignId === campaignId) {
            names[c.controllerUserId] = c.name;
          }
        }
        setAuthorNames(names);
      })
      .catch(() => {
        if (!cancelled) {
          setActingCharacterId(null);
          setAuthorNames({});
        }
      });
    return () => {
      cancelled = true;
    };
  }, [api, campaignId]);

  // CAMPAIGN read — refreshed when the seqToken advances so the clock/weather
  // badge reflects a committed turn's time/weather change (the same refetch-on-
  // seqToken approximation MapPanel uses). Split from the roster read (Medium #8)
  // so a transient failure here only blanks the read-only badge, never the acting
  // character or the journal bylines.
  useEffect(() => {
    let cancelled = false;
    api
      .listCampaigns()
      .then((campaigns) => {
        if (cancelled) return;
        const found = campaigns.find((c) => c.campaignId === campaignId);
        // `name` is optional on CampaignState (older campaigns lack it).
        setCampaignName(found?.name || undefined);
        setCampaign(found);
      })
      .catch(() => {
        if (!cancelled) {
          setCampaignName(undefined);
          setCampaign(undefined);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [api, campaignId, session.latestSeqToken]);

  const send = async (text: string, mode: MessageMode) => {
    // Free-flow lines and actions both go through the turn-ingress (there is no
    // separate /chat route). Generate a client idempotency turnId and carry the
    // last observed seqToken (0 before the first result, R18.5). The player's
    // line and the narrated result BOTH arrive over the WS push channel keyed by
    // the server's conversation-log seq — so we do NOT emit an optimistic local
    // echo here (the client has no server seq to dedupe against, and echoing
    // would double-render once the server push lands).
    const turnId =
      globalThis.crypto?.randomUUID?.() ??
      `turn_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    // Publish the in-flight turn as the telemetry anchor so a crash DURING or
    // right after this turn (the riskiest moment — a fresh code path acting on
    // new server state) is attributable to the exact turn, not just the
    // campaign. Kept alongside the campaignId the mount effect already set.
    setTelemetryAnchor({ campaignId, turnId });
    setActionError(null);
    try {
      await api.submitTurn({
        campaignId,
        turnId,
        seqToken: session.latestSeqToken ?? 0,
        action: text,
        // Only send a mode when it is the non-default `ooc`; an IC line omits it
        // so the wire stays identical to the pre-toggle request for the common case.
        ...(mode === "ooc" ? { mode } : {}),
      });
    } catch (err) {
      // A refused turn (402 insufficient credits, 409 paused, 403 moderation, …)
      // surfaces as a friendly line — never an uncaught rejection. The server's
      // ApiError message is player-safe; other throws fall back to generic text.
      setActionError(
        friendlyErrorMessage(err, "Couldn't send that just now. Please try again."),
      );
    } finally {
      // RETIRE the turn anchor once the submit settles (#6). The `turnId` is
      // meant to attribute a crash to the brief, risky window AROUND this submit;
      // leaving it pinned would misattribute an UNRELATED crash minutes later
      // (while the player idles, reads back, or starts a different turn) to this
      // now-completed turn. Fall back to the campaign-only anchor the mount
      // effect holds, which the next `send` overwrites with its own turnId.
      // GUARDED by `mountedRef` (round-4 Low #9): if the player navigated away
      // while this turn was in flight, the unmount cleanup already CLEARED the
      // anchor — re-pinning `{campaignId}` here would resurrect a stale anchor
      // on a campaign the player has left. Only retire while still mounted.
      if (mountedRef.current) setTelemetryAnchor({ campaignId });
    }
  };

  const rollContext: RollContext | null = actingCharacterId
    ? {
        campaignId,
        // The player's real acting character in this campaign (resolved above).
        // A d20 check is the common Phase-1 case; the roller offers both per-roll
        // choices (R2.1). A richer multi-die roller is deferred (future-todos).
        characterId: actingCharacterId,
        dice: "1d20",
      }
    : null;

  const onRoll = async (req: DiceRollRequest) => {
    const res = await api.rollDice(req);
    // The dice endpoint appends the roll to the campaign audit log and the
    // server fans it back over the free-flow push (folded by seq into the
    // journal). We do NOT also record a local echo — the server's audit seq is
    // authoritative and a local `roll:${seq}` entry would not dedupe against the
    // server's `chat:${seq}` push, double-rendering the roll (R2.3).
    return res;
  };

  // APPEND a resolved roll's tag to the composer draft (parts #2/#3 of the
  // player's report: a roll resolved server-side but went nowhere visible in
  // the composer). Append rather than replace so a half-typed line is preserved
  // — the player can write before and/or after the tag, then send on their own
  // terms. One space separates the tag from existing text unless the draft
  // already ends in whitespace.
  const appendRollTag = (req: DiceRollRequest, res: DiceRollResponse) => {
    const tag = rollChatTag(req, res.total);
    setDraft((prev) => (prev.length === 0 ? tag : /\s$/.test(prev) ? `${prev}${tag}` : `${prev} ${tag}`));
  };

  return (
    <section aria-label="Play" className="play-view">
      <p>
        <Link to="/campaigns">← Back to campaigns</Link>
        {" · "}
        <Link
          to={`/campaigns/${campaignId}/green-room`}
          data-testid="open-green-room"
        >
          Green Room
        </Link>
        {actingCharacterId ? (
          <>
            {" · "}
            <Link
              to={`/characters/${actingCharacterId}`}
              data-testid="view-character-sheet"
            >
              View your character sheet
            </Link>
          </>
        ) : null}
      </p>
      <h1>Playing {campaignName ?? `campaign ${campaignId}`}</h1>

      {/* In-world clock + day-phase + current weather (read-only projection of
          the already-fetched campaign state; refreshes on each committed turn). */}
      <TimeWeatherBadge campaign={campaign} />

      <SessionStatus
        writing={session.writing}
        coldStartWaiting={session.coldStartWaiting}
        retry={session.retry}
        status={session.status}
        onRetryAcknowledge={session.clearRetry}
      />
      {/* The spatial map (Big Phase 1.5): an inline mini-map + a full-screen
          viewer/library. Best-effort — a read failure degrades to "no map yet"
          and never disturbs play. The refreshKey is the session's DEDICATED
          map-refresh nonce (NOT the save-concurrency seqToken): it bumps on every
          result push, on a map_updated push (incl. a background ring-ahead
          pre-author that advances no result token), and on reconnect hydrate — so
          a discovered room / opened door / moved party shows up on its own
          instead of staying stale until an unrelated event forces a refetch. */}
      <MapPanel api={api} campaignId={campaignId} refreshKey={session.mapRefreshNonce} />
      <Journal
        entries={session.journal}
        resolveAuthor={(author) => authorNames[author] ?? author}
      />
      {actionError ? (
        <p className="action-error" role="alert" data-testid="action-error">
          {actionError}
        </p>
      ) : null}
      <FreeFlowChat onSend={send} value={draft} onChange={setDraft} />
      {rollContext ? (
        <DiceRoller context={rollContext} onRoll={onRoll} onResult={appendRollTag} />
      ) : (
        <p className="dice-roller-unavailable">
          Bring a character into this campaign to roll dice.
        </p>
      )}
      {/* Report-an-issue — anchored to THIS campaign + the current log position
          (the server stamps the anchor; the client sends only the text). */}
      <IssueReporter
        onReport={(text) => api.reportCampaignIssue(campaignId, { text }).then(() => undefined)}
        contextHint="This report will be linked to your current campaign so it can be investigated in context."
      />
    </section>
  );
}
